import type { ReactNode } from 'react';
import { useProjectFileUploadGuard } from '../model/useProjectFileUploadGuard';
import type { ResolvedUpload } from '../model/types';
import { UploadConflictModal } from './UploadConflictModal';

interface ProjectFileUploadGuardRenderProps {
  /** Hand the dropped/selected files to the guard. Runs the conflict check. */
  submit: (files: File[]) => void;
  /** True while the pre-upload conflict check is in flight. */
  checking: boolean;
}

interface ProjectFileUploadGuardProps {
  projectId: string;
  /** Target folder; omit for the project root. */
  folderId?: string;
  /**
   * Called with the resolved set once there are no conflicts (or the user has
   * resolved them).
   */
  onResolved: (items: ResolvedUpload[]) => void;
  children: (props: ProjectFileUploadGuardRenderProps) => ReactNode;
}

/**
 * Headless gate that sits in front of an upload trigger
 * (dropzone or button). It runs a pre-upload filename conflict check and, when
 * there are collisions, opens a resolution modal (rename / upload-as-version)
 * before handing the resolved set back via `onResolved`. With no conflicts it
 * passes straight through.
 */
export function ProjectFileUploadGuard(props: ProjectFileUploadGuardProps) {
  const { projectId, folderId, onResolved, children } = props;
  const guard = useProjectFileUploadGuard({ projectId, folderId, onResolved });

  return (
    <>
      {children({ submit: guard.submit, checking: guard.checking })}
      <UploadConflictModal
        open={guard.isResolving}
        items={guard.items}
        canConfirm={guard.canConfirm}
        onUpdateResolutionOption={guard.updateResolutionOption}
        onRename={guard.rename}
        onRemove={guard.remove}
        onConfirm={guard.confirm}
        onCancel={guard.cancel}
      />
    </>
  );
}
